import { IntlProvider, FormattedMessage } from '@godaddy/react-mintl';
import { URL } from '@ux/util';
import { default as AllProductsNav } from '@ux/mobile-nav';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useBreakpoints, isMobile } from '@ux/component-utilities';
import { breakpoints, golf } from './enums';
import {
  urlMatches,
  linkListBuilder,
  trayMenusBuilder
} from './utils';

// eslint-disable-next-line complexity, max-statements
const findParent = (opts) => {
  const { url, id, items, parent } = opts;
  let { firstMatch } = opts;

  if (!url || !items) {
    return;
  }

  if (Array.isArray(items)) {
    for (let i = 0, len = items.length; i < len; i++) {
      const result = findParent({
        url,
        id,
        items: items[i],
        firstMatch,
        parent
      });
      if (result) {
        if (result.isFullMatch) {
          return result;
        }
        if (i === len - 1) {
          return result;
        } else if (!firstMatch) {
          firstMatch = result.activeHeading;
        }
      }
    }
  } else {
    if (items.href && urlMatches(url, items.href, true)) {
      if (!firstMatch) {
        firstMatch = parent || items;
      }
      if (!id || id === items.id) {
        return {
          isFullMatch: true,
          activeHeading: parent || items
        };
      }
    }

    if (items.children) {
      return findParent({
        url,
        id,
        items: items.children,
        firstMatch,
        parent: items
      });
    }

    if (firstMatch) {
      return {
        isFullMatch: false,
        activeHeading: firstMatch
      };
    }
  }
};

const initState = ({ currentHref, activeCaption, navigation }) => {
  if (typeof document !== 'undefined' && !currentHref) {
    activeCaption = window.sessionStorage.getItem('activeCaption');
    currentHref = document.location.href;
  }

  if (currentHref) {
    const currentUrl = new URL(currentHref, true);
    const qsCurrentHref = currentUrl.query.currentPage;
    currentHref = qsCurrentHref || currentUrl.href;
  }

  const active = findParent({
    url: currentHref,
    id: activeCaption,
    items: navigation,
    firstMatch: false
  });

  return {
    currentHref,
    activeHeading: active && active.activeHeading
  };
};

export const ProductNavComponent = ({
  activeCaption,
  breakpoint,
  currentHref: currentHrefProp = '',
  isReseller = false,
  market = 'en-US',
  messages,
  navigation,
  navigationRight = [],
  showPrimaryNav = true,
  urlArgs,
  urls
}) => {
  const [{ currentHref, activeHeading }] = useState(() => initState({
    currentHref: currentHrefProp,
    activeCaption,
    navigation
  }));
  const [openTrays, setOpenTrays] = useState({});

  const handleTrayToggle = (trayId, open) => {
    setOpenTrays(prev => ({ ...prev, [trayId]: open }));
  };

  const buildMainNav = () => {
    const navs = navigation.filter(item =>
      item.title && (item.children || item.href) && !item.onlyMobile
    );

    return trayMenusBuilder(navs, {
      urlArgs,
      className: 'topnav-item',
      classes: {
        isPro: 'flag-pro',
        isFree: 'flag-free',
        isNew: 'flag-new',
        onSale: 'flag-sale',
        tout: 'flag-sale',
        productTitle: 'product-title'
      },
      openTrays,
      onTrayToggle: handleTrayToggle
    });
  };

  const buildNavRight = (navRightClassName) => {
    if (!navigationRight) {
      return null;
    }

    return trayMenusBuilder(navigationRight, {
      className: classnames(navRightClassName, 'topnav-item'),
      classes: {
        isPro: 'flag-pro',
        isFree: 'flag-free',
        isNew: 'flag-new',
        onSale: 'flag-sale',
        tout: 'flag-sale',
        productTitle: 'product-title'
      },
      eidPrefix: 'uxp.hyd.product_nav',
      urlArgs,
      openTrays,
      onTrayToggle: handleTrayToggle
    });
  };

  // eslint-disable-next-line max-statements, complexity
  const buildSecondaryNav = () => {
    let active;

    if (!activeHeading || !activeHeading.children) {
      return [];
    }

    for (let i = 0; i < activeHeading.children.length; i++) {
      const item = activeHeading.children[i];
      if (urlMatches(currentHref, item.href, true)) {
        if (item.options && item.options.hideSecondaryNav) {
          return [];
        }
        active = item.id;
      }
    }

    const subnav = linkListBuilder(activeHeading.children, {
      className: 'secondarynav-item',
      activeClass: 'active',
      active,
      eidPrefix: 'uxp.hyd.secondary_nav',
      urlArgs
    });

    const { title, id } = activeHeading;
    if (title) {
      subnav.unshift(
        <li
          key={ title }
          id={ id }
          className='secondarynav-item'>
          {title}
        </li>
      );
    }

    return subnav;
  };

  const renderDesktopPrimaryNav = () => {
    if (!showPrimaryNav) {
      return null;
    }

    const topNavList = buildMainNav();
    const navRight = buildNavRight();

    return (
      <nav className='topnav'>
        <div className='product-nav-container'>
          <div className='product-nav-row'>
            <ul className='topnav-items'>
              {topNavList}
            </ul>
            <ul className='topnav-right'>
              {navRight}
            </ul>
          </div>
        </div>
      </nav>
    );
  };

  const renderDesktopSecondaryNav = () => {
    const secondaryNavList = buildSecondaryNav();

    if (!secondaryNavList.length) {
      return null;
    }

    return (
      <nav className='secondarynav'>
        <div className='product-nav-container'>
          <div className='product-nav-flex'>
            <div>
              <ul className='secondarynav-items'>
                {secondaryNavList}
              </ul>
            </div>
          </div>
        </div>
      </nav>
    );
  };

  const renderDesktop = () => (
    <section>
      {renderDesktopPrimaryNav()}
      {renderDesktopSecondaryNav()}
    </section>
  );

  const renderMobile = () => {
    if (!showPrimaryNav) {
      return null;
    }

    const navs = [];
    const navRight = buildNavRight('topnav-promos-link-mobile');

    navigation.forEach(item => {
      const navItem = (item.leftnav && item.leftnav.href && !item.href)
        ? { ...item, href: item.leftnav.href }
        : item;

      if (navItem.title) {
        navs.push(navItem);
      }
    });

    return (
      <div>
        <nav
          className='topnav'
          style={{ overflow: 'visible' }}>
          <div className='product-nav-container'>
            <div className='product-nav-row'>
              <AllProductsNav
                nav={ navs }
                menuTitle={ <FormattedMessage id={ golf.allProducts } /> }
                market={ market }
                messages={ messages }
                urls={ urls }
                currentHref={ currentHref }
                activeHeading={ activeHeading }
                showHomeLink={ isReseller }
                eidPrefix='uxp.hyd.product_nav'
                urlArgs={ urlArgs }
                menuButtonEid={ 'uxp.hyd.all_products_menu_open.button.click' }
              />
              <ul className='mobile-nav-right'>
                {navRight}
              </ul>
            </div>
          </div>
        </nav>
      </div>
    );
  };

  const content = isMobile(breakpoint) || breakpoint === 'tablet'
    ? renderMobile()
    : renderDesktop();

  return (
    <IntlProvider messages={ messages }>
      <div className='top'>
        {content}
      </div>
    </IntlProvider>
  );
};

ProductNavComponent.propTypes = {
  activeCaption: PropTypes.string,
  currentHref: PropTypes.string,
  market: PropTypes.string,
  messages: PropTypes.object.isRequired,
  navigation: PropTypes.array.isRequired,
  navigationRight: PropTypes.array,
  showPrimaryNav: PropTypes.bool,
  isReseller: PropTypes.bool,
  urlArgs: PropTypes.object,
  urls: PropTypes.object,
  breakpoint: PropTypes.string.isRequired
};

const ProductNavWrapper = (props) => {
  const { breakpoint } = useBreakpoints(...breakpoints);
  return <ProductNavComponent { ...props } breakpoint={ breakpoint } />;
};

export default ProductNavWrapper;
